<template>
  <a-app>
    <context-holder />
    <a-drawer
      :width="720"
      :open="open"
      class="resident-notification-drawer"
      :body-style="{ paddingBottom: '96px' }"
      :header-style="{ padding: '32px 40px', border: 'none' }"
      :footer-style="{ padding: '20px 40px 40px 40px' }"
      :closable="true"
      @close="closeDrawer"
    >
      <a-divider class="resident-notification-drawer__divider" />

      <template #title>
        <span class="resident-notification-drawer__title">Уведомление</span>
      </template>

      <template #closeIcon>
        <span class="resident-notification-drawer__close-trigger">
          <CloseOutlined />
        </span>
      </template>

      <div v-if="loading" class="resident-notification-drawer__loading">Загрузка...</div>

      <template v-else>
        <section class="resident-notification-drawer__meta">
          <div class="field-with-label">
            <span class="field-with-label__label">Тема</span>
            <span class="field-with-label__value">{{ detail.title }}</span>
          </div>

          <div class="field-with-label">
            <span class="field-with-label__label">Дата / время</span>
            <span class="field-with-label__value">{{ detail.date }}</span>
          </div>
        </section>

        <section class="resident-notification-drawer__description">
          <span class="field-with-label__label">Описание проекта</span>
          <div class="resident-notification-drawer__description-text">
            <ContentTagsHtml :html="detail.description" />
          </div>
        </section>

        <section v-if="detail.files.length" class="resident-notification-drawer__files">
          <span class="field-with-label__label">Прикреплённые файлы</span>
          <ul class="resident-notification-drawer__file-list">
            <li
              v-for="(file, index) in detail.files"
              :key="`${file.url}-${file.name}-${index}`"
              class="resident-notification-drawer__file-item"
            >
              <img
                :src="getDocFormatIconPath(file.name)"
                alt=""
                class="resident-notification-drawer__file-icon"
              />
              <div class="resident-notification-drawer__file-content">
                <a :href="file.url" target="_blank" rel="noopener noreferrer" download>
                  {{ file.name }}
                </a>
                <span v-if="file.type || file.size">
                  {{ [file.type, file.size].filter(Boolean).join(' / ') }}
                </span>
              </div>
            </li>
          </ul>
        </section>

        <section class="resident-notification-drawer__reply">
          <a-form layout="vertical" class="resident-notification-drawer__reply-form">
            <a-form-item
              class="stc-ant-field-skin resident-notification-drawer__reply-field"
              label="Ответ*"
            >
              <a-textarea
                v-model:value="reply"
                :rows="6"
                :maxlength="REPLY_MAX_LENGTH"
                placeholder="Введите ответ"
              />
              <div class="resident-notification-drawer__reply-footer">
                <span class="resident-notification-drawer__reply-note">*не обязателен</span>
                <span class="resident-notification-drawer__reply-counter">
                  {{ reply.length }} / {{ REPLY_MAX_LENGTH }}
                </span>
              </div>
            </a-form-item>
          </a-form>
        </section>
      </template>

      <template #footer>
        <div class="resident-notification-drawer__footer">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="loading || isSubmitting || !canSubmitReply"
            @click="submitReply"
          >
            {{ isSubmitting ? 'Отправка...' : 'Отправить ответ' }}
          </button>
        </div>
      </template>
    </a-drawer>
  </a-app>
</template>

<script setup lang="ts">
import { computed, h, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import StcNotificationContent from '@/js/components/stc-notification/stc-notification-content.vue';
import ContentTagsHtml from '@/js/components/content-tags/content-tags-html.vue';
import '@/js/components/stc-notification/stc-notification.scss';
import type { NotificationDrawerPayload } from './types';
import {
  fetchNotificationDetail as fetchNotificationDetailApi,
  markNotificationRead,
  replyToNotification,
} from '../resident-notifications/notification-api';
import {
  formatNotificationDate,
  normalizeNotificationFiles,
} from '../resident-notifications/map-notification';
import type { NotificationFile } from '../resident-notifications/types';
import { getDocFormatIconPath } from '@/js/utils/file-upload-utils';

const REPLY_MAX_LENGTH = 512;
const REPLY_SUCCESS_MESSAGE = 'Ваш ответ отправлен';
const REPLY_ERROR_MESSAGE = 'Не удалось отправить ответ';

const [notificationApi, contextHolder] = notification.useNotification();

const open = ref(false);
const loading = ref(false);
const isSubmitting = ref(false);
const reply = ref('');

const detail = reactive({
  id: undefined as number | undefined,
  title: '',
  date: '',
  description: '',
  files: [] as NotificationFile[],
});

const canSubmitReply = computed(() => reply.value.trim().length > 0);

const NOTIFICATION_ICON_SIZE = 24;

function showReplySuccessNotification(description = REPLY_SUCCESS_MESSAGE): void {
  notificationApi.open({
    message: h(StcNotificationContent, {
      type: 'success',
      description,
      iconSize: NOTIFICATION_ICON_SIZE,
    }),
    placement: 'topRight',
    duration: 10,
    class: 'stc-notification',
    icon: null,
  });
}

function showReplyErrorNotification(description = REPLY_ERROR_MESSAGE): void {
  notificationApi.open({
    message: h(StcNotificationContent, {
      type: 'error',
      description,
      iconSize: NOTIFICATION_ICON_SIZE,
    }),
    placement: 'topRight',
    duration: 10,
    class: 'stc-notification',
    icon: null,
  });
}

function resetDrawer(): void {
  detail.id = undefined;
  detail.title = '';
  detail.date = '';
  detail.description = '';
  detail.files = [];
  reply.value = '';
  loading.value = false;
  isSubmitting.value = false;
}

function closeDrawer(): void {
  open.value = false;
  resetDrawer();
}

function applyPayload(payload: NotificationDrawerPayload): void {
  detail.id = payload.id;
  detail.title = payload.title;
  detail.date = payload.date;
  detail.description = payload.description;
}

async function loadNotificationDetail(id: number): Promise<void> {
  loading.value = true;

  try {
    const [data] = await Promise.all([fetchNotificationDetailApi(id), markNotificationRead(id)]);

    detail.title = data.subject ?? detail.title;
    detail.date = data.createdAt ? formatNotificationDate(data.createdAt) : detail.date;
    detail.description = data.message ?? detail.description;
    detail.files = normalizeNotificationFiles(data.files);

    window.dispatchEvent(new CustomEvent('residentNotifications:changed'));
  } catch (error) {
    showReplyErrorNotification(
      error instanceof Error ? error.message : 'Не удалось загрузить уведомление',
    );
  } finally {
    loading.value = false;
  }
}

async function showDrawer(payload: NotificationDrawerPayload): Promise<void> {
  resetDrawer();
  applyPayload(payload);
  open.value = true;

  if (payload.id) {
    await loadNotificationDetail(payload.id);
  }
}

async function submitReply(): Promise<void> {
  const message = reply.value.trim();
  if (!message || isSubmitting.value) return;

  if (!detail.id) {
    showReplySuccessNotification();
    closeDrawer();
    return;
  }

  isSubmitting.value = true;

  try {
    await replyToNotification(detail.id, {
      subject: `Re: ${detail.title}`,
      message,
    });

    showReplySuccessNotification();
    window.dispatchEvent(new CustomEvent('residentNotifications:changed'));
    closeDrawer();
  } catch (error) {
    showReplyErrorNotification(error instanceof Error ? error.message : REPLY_ERROR_MESSAGE);
  } finally {
    isSubmitting.value = false;
  }
}

defineExpose({ open: showDrawer });
</script>
